import React, { useEffect, useState } from 'react';
import useExpertStore from '../store/useExpertStore';
import ExpertCard from '../components/ExpertCard';
import ExpertForm from '../components/ExpertForm';
import ViewToggle from '../components/ViewToggle';
import { useNavigate } from 'react-router-dom';
import useChatStore from '../store/useChatStore';
import { motion, AnimatePresence } from 'framer-motion';

const PlusIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" /></svg>;
const AlertCircleIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>;
const BotIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 3v1.5M4.5 8.25H3m18 0h-1.5M4.5 12H3m18 0h-1.5m-15 3.75H3m18 0h-1.5M8.25 19.5V21M12 3v1.5m0 15V21m3.75-18v1.5m0 15V21m-9-1.5h10.5a2.25 2.25 0 002.25-2.25V6.75a2.25 2.25 0 00-2.25-2.25H6.75A2.25 2.25 0 004.5 6.75v10.5a2.25 2.25 0 002.25 2.25zm.75-12h9v9h-9v-9z" /></svg>;
const Spinner = () => <svg className="animate-spin h-8 w-8 text-indigo-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>;

const ExpertMarketplace = () => {
    const { experts, loading, error, fetchExperts } = useExpertStore();
    const { createChat } = useChatStore();
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [selectedExpert, setSelectedExpert] = useState(null);
    const [viewMode, setViewMode] = useState(() => localStorage.getItem('agents_view_mode') || 'grid');
    const navigate = useNavigate();

    useEffect(() => {
        localStorage.setItem('agents_view_mode', viewMode);
    }, [viewMode]);

    useEffect(() => {
        fetchExperts();
    }, [fetchExperts]);

    const handleChat = async (expertId) => {
        const expert = experts.find(e => e.id === expertId);
        if (expert) {
            try {
                const chatId = await createChat(expert.name, {}, null); // Simple starting chat
                navigate(`/chat/${chatId}`);
            } catch (error) {
                console.error("Failed to create chat:", error);
            }
        }
    };

    const handleEdit = (expert) => {
        setSelectedExpert(expert);
        setIsFormOpen(true);
    };

    const handleCloseForm = () => {
        setIsFormOpen(false);
        setSelectedExpert(null);
    };

    const handleCreateNew = () => {
        setSelectedExpert(null);
        setIsFormOpen(true);
    }

    return (
        <div className="pb-20 min-h-full bg-[var(--bg-main)] flex flex-col font-sans selection:bg-[var(--accent-primary)]/20">
            {error && (
                <div className="bg-red-500/10 border-b border-red-500/20 px-6 py-3 flex items-start gap-3 animate-in slide-in-from-top duration-300">
                    <AlertCircleIcon className="w-5 h-5 text-red-400 mt-0.5" />
                    <p className="text-sm font-medium text-red-300">Forge Error: {error}</p>
                </div>
            )}

            <header className="px-4 md:px-8 py-8 md:py-14 max-w-7xl mx-auto w-full flex flex-col md:flex-row md:justify-between md:items-end gap-6">
                <div className="space-y-1">
                    <h1 className="text-3xl md:text-4xl font-light text-[var(--text-main)] tracking-tighter">Agents</h1>
                    <p className="text-sm text-[var(--text-muted)] font-medium">Design and deploy custom AI personas tailored for your specific workflows.</p>
                </div>

                <div className="flex items-center gap-3">
                    <button
                        onClick={handleCreateNew}
                        className="group relative flex items-center gap-2 px-4 py-2 bg-[var(--text-main)] text-[var(--bg-main)] rounded-full font-semibold transition-all hover:bg-[var(--accent-hover)] active:scale-95 shadow-lg shadow-[var(--accent-primary)]/5"
                    >
                        <PlusIcon className="w-3.5 h-3.5" />
                        <span className="text-[12px] tracking-tight">New Agent</span>
                    </button>
                    <ViewToggle mode={viewMode} onChange={setViewMode} />
                </div>
            </header>

            <div className="px-4 md:px-8 max-w-7xl mx-auto w-full flex-1">
                <AnimatePresence mode="wait">
                    {experts.length === 0 && !loading ? (
                        <motion.div
                            key="empty-state"
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                            className="flex flex-col items-center justify-center py-32 text-center"
                        >
                            <div className="w-24 h-24 bg-[var(--bg-tertiary)]/30 backdrop-blur-md rounded-3xl flex items-center justify-center mb-6 border border-[var(--border-subtle)] shadow-2xl relative group">
                                <div className="absolute inset-0 bg-gradient-to-tr from-[var(--accent-primary)]/10 to-transparent rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity" />
                                <BotIcon className="w-10 h-10 text-[var(--text-dim)]" />
                            </div>
                            <h2 className="text-xl font-semibold text-[var(--text-main)] mb-2 tracking-tight">The forge is cold</h2>
                            <p className="text-sm text-[var(--text-muted)] max-w-xs leading-relaxed">
                                No active agents found. Build your first custom AI persona to begin collaborating with Omni.
                            </p>
                        </motion.div>
                    ) : (
                        <motion.div
                            layout
                            className={viewMode === 'grid'
                                ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-20 auto-rows-fr"
                                : "flex flex-col gap-3 mb-20"}
                        >
                            {experts.map((expert) => (
                                <motion.div
                                    key={expert.id}
                                    layout
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className={viewMode === 'grid' ? "h-full" : ""}
                                >
                                    {viewMode === 'grid' ? (
                                        <ExpertCard
                                            expert={expert}
                                            onEdit={() => handleEdit(expert)}
                                        />
                                    ) : (
                                        <div
                                            onClick={() => handleEdit(expert)}
                                            className="group relative flex items-center justify-between p-3.5 bg-[var(--bg-secondary)] border border-[var(--border-subtle)] rounded-xl hover:border-[var(--border-medium)] transition-all duration-200 shadow-sm cursor-pointer pr-12"
                                        >
                                            <div className="flex items-center gap-4 flex-1 min-w-0">
                                                {expert.imageUrl ? (
                                                    <img src={expert.imageUrl} alt={expert.name} className="w-8 h-8 rounded-lg object-cover shrink-0" />
                                                ) : (
                                                    <div className="w-8 h-8 rounded-lg bg-[var(--text-main)] text-[var(--bg-main)] flex items-center justify-center shrink-0">
                                                        <BotIcon className="w-4 h-4" />
                                                    </div>
                                                )}
                                                <div className="flex-1 min-w-0">
                                                    <h3 className="text-[14px] font-medium text-[var(--text-main)] truncate leading-tight group-hover:text-[var(--accent-primary)] transition-colors">
                                                        {expert.name}
                                                    </h3>
                                                    <p className="text-[11px] text-[var(--text-dim)] truncate mt-0.5">
                                                        {expert.description || "Custom AI persona for specific workflows."}
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="flex items-center gap-4 shrink-0">
                                                <span className="text-[10px] font-bold text-[var(--text-dim)] uppercase tracking-widest bg-[var(--bg-tertiary)]/50 px-2 py-0.5 rounded border border-[var(--border-subtle)] leading-none text-center min-w-[60px]">
                                                    Agent
                                                </span>
                                            </div>

                                            <div className="absolute top-1/2 -translate-y-1/2 right-3 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-all duration-300">
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleEdit(expert);
                                                    }}
                                                    className="p-1.5 rounded-md bg-white/5 hover:bg-white/10 text-[var(--text-muted)] hover:text-white transition-all shadow-sm"
                                                    title="Settings"
                                                >
                                                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </motion.div>
                            ))}
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            <ExpertForm
                open={isFormOpen}
                onClose={handleCloseForm}
                expertToEdit={selectedExpert}
            />
        </div>
    );
};

export default ExpertMarketplace;
